import { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Icon,
  Stack,
  Text,
  Tag,
  Spinner,
  useToast,
  Input,
  Select,
  Button
} from '@chakra-ui/react';
import {
  FaBuilding,
  FaMapMarkerAlt,
  FaDollarSign,
  FaCalendarAlt
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import useSecureApi from '../Authentication/UseSecureApi';

// ✅ Default fallback jobs
const defaultJobs = [
  {
    id: '1',
    title: 'Frontend Developer',
    company: 'Telivi IT',
    type: 'Full-time',
    location: 'Remote, CA',
    salaryRange: '100000 - $140000',
    description: 'Join us to build cutting-edge React using AI interfaces.',
    postedDate: new Date().toISOString(),
    skills: ['React', 'Node', 'AI', 'JavaScript', 'CSS']
  },
  {
    id: '2',
    title: 'Backend Developer',
    company: 'Telivi IT',
    type: 'Contract',
    location: 'Remote',
    salaryRange: '80,000 - $120,000',
    description: 'Work on scalable Spring boot and Node.js services.',
    postedDate: new Date().toISOString(),
    skills: ['Node.js', 'Spring boot', 'Express', 'MongoDB', 'PostgreSQL']
  }
];

const JobListingsPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [isFetchingJobs, setIsFetchingJobs] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 5;
  const api = useSecureApi();

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const { data } = await api.get('/api/jobs');
        setJobs(Array.isArray(data) ? data : defaultJobs);
      } catch (error) {
        toast({
          title: 'Error loading jobs',
          description: 'Using fallback listings instead.',
          status: 'warning',
          duration: 3000,
          isClosable: true
        });
        setJobs(defaultJobs);
      } finally {
        setIsFetchingJobs(false);
      }
    };

    fetchJobs();
  }, [toast, api]);

  const filteredJobs = jobs.filter(job => {
    const matchesSearch =
      job.title.toLowerCase().includes(search.toLowerCase()) ||
      job.company.toLowerCase().includes(search.toLowerCase()) ||
      job.skills.join(',').toLowerCase().includes(search.toLowerCase());
    const matchesType = filterType ? job.type === filterType : true;
    return matchesSearch && matchesType;
  });

  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);

  return (
    <Box maxW="7xl" mx="auto" px={4} py={8}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading>Job Listings</Heading>
      </Flex>

      <Flex gap={4} mb={6} wrap="wrap">
        <Input
          placeholder="Search by title, company, or skills"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          maxW="300px"
        />
        <Select
          placeholder="Filter by type"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          maxW="200px"
        >
          <option value="Full-time">Full-time</option>
          <option value="Part-time">Part-time</option>
          <option value="Contract">Contract</option>
        </Select>
      </Flex>

      {isFetchingJobs ? (
        <Flex justify="center" align="center" py={20}>
          <Spinner size="xl" />
        </Flex>
      ) : filteredJobs.length === 0 ? (
        <Text>No job listings found.</Text>
      ) : (
        <Stack spacing={6}>
          {currentJobs.map(job => (
            <Box
              key={job.id}
              p={6}
              borderWidth={1}
              borderRadius="lg"
              boxShadow="md"
              position="relative"
            >
              <Heading size="md" mb={2}>{job.title}</Heading>
              <Flex align="center" mb={1}><Icon as={FaBuilding} mr={2} />{job.company}</Flex>
              <Flex align="center" mb={1}><Icon as={FaMapMarkerAlt} mr={2} />{job.location}</Flex>
              <Flex align="center" mb={1}><Icon as={FaDollarSign} mr={2} />{job.salaryRange}</Flex>
              <Flex align="center" mb={1}><Icon as={FaCalendarAlt} mr={2} />{new Date(job.postedDate).toLocaleDateString()}</Flex>
              <Text mt={2}>{job.description}</Text>
              <Flex mt={4} gap={2} flexWrap="wrap">
                {job.skills.map((skill, idx) => (
                  <Tag key={idx} colorScheme="teal">{skill}</Tag>
                ))}
              </Flex>
              <Button
                colorScheme="blue"
                position="absolute"
                top={4}
                right={4}
                onClick={() => navigate(`/apply/${job.id}`)}
              >
                Apply
              </Button>
            </Box>
          ))}
        </Stack>
      )}

      {filteredJobs.length > jobsPerPage && (
        <Flex mt={8} justify="center" gap={4}>
          <Button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            isDisabled={currentPage === 1}
          >
            Previous
          </Button>
          <Text alignSelf="center">
            Page {currentPage} of {totalPages}
          </Text>
          <Button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            isDisabled={currentPage === totalPages}
          >
            Next
          </Button>
        </Flex>
      )}
    </Box>
  );
};

export default JobListingsPage;
